/**
 * Validation utilities for stored RoundTable data
 * Reuses the validation logic from contentValidation.ts
 */

import { validateContent, ValidationCheckResult, ValidationIssue } from './importDetails/contentValidation';

export interface RoundTable {
    id: number;
    name: string;
    number: number;
    iban: string;
    bic: string;
    kontoInhaber: string;
    bankName: string;
    billingName: string;
    billingEmail: string;
    billingPlz: string;
    billingCity: string;
    billingStreet: string;
    billingStreetNumber: string;
}

export interface RoundTableValidationResult {
    isValid: boolean;
    checks: ValidationCheckResult[];
}

/**
 * Converts RoundTable data to the Excel-like format expected by contentValidation
 * This allows us to reuse existing validation logic
 */
const convertRoundTableToExcelFormat = (roundTables: RoundTable[]): Record<string, any>[] => {
    return roundTables.map((rt) => ({
        '[Club] Name': rt.name,
        '[Club] Subdomain': `${rt.number}-${rt.name.toLowerCase().replace(/\s+/g, '-')}`,
        '[Bank Account] IBAN': rt.iban,
        '[Bank Account] BIC': rt.bic,
        '[Bank Account] Owner': rt.kontoInhaber,
        '[FinOrg] Name': rt.bankName,
        '[Treasurer] Name': rt.billingName,
        '[Treasurer] Email': rt.billingEmail,
        '[Treasurer] Postal Code': rt.billingPlz,
        '[Treasurer] City': rt.billingCity,
        '[Treasurer] Street1': rt.billingStreet,
        '[Treasurer] Street2': rt.billingStreetNumber,
    }));
};

/**
 * Validates stored RoundTable data for IBAN, BIC, and duplicate issues
 * @param roundTables - Array of RoundTable records from the database
 * @returns Validation result with all check results
 */
export const validateRoundTables = (roundTables: RoundTable[]): RoundTableValidationResult => {
    if (roundTables.length === 0) {
        return { isValid: true, checks: [] };
    }

    // Convert RoundTable data to Excel format for validation
    const excelFormatData = convertRoundTableToExcelFormat(roundTables);

    // Reuse existing validation logic
    const validationResult = validateContent(excelFormatData);

    return {
        isValid: validationResult.isValid,
        checks: validationResult.checks,
    };
};
